import { useEffect, useMemo, useState } from "react"
import { useParams, useLocation } from "react-router-dom"

import { Accordion, Loading, SectionHeading, FormLabel, Select, FormError } from "../../components/common"
import { useForm, useNav, useNavigateWithFilters, useUnsavedChangesGuard, useFormErrors } from "../../components/hooks"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb"
import { APIS } from "../../config/apis"
import { isNull, isValidString } from "../../utils/validator";
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { TablaColumnas } from './components'
import { LoadColumnas } from './utils';
import { CloneObject, ComparePeriodos } from "../../utils/helpers";
import TablaVersionesGrid from "../../components/controls/TablaVersionesGrid";
import { addDayToDate, getDateNow } from "../../utils/convert";


const TablaDefinicionView = () => {
    const params = useParams()
    const location = useLocation()
    const previousFilters = location.state?.filters
    const navigate = useNav()
    const navigateWithFilters = useNavigateWithFilters(() => previousFilters || null)

    const entityInit = {
        tablaDefinicion: {
            id: 0,
            codigo: '',
            nombre: '',
            descripcion: '',
            nombre1: '',
            nombre2: '',
            nombre3: '',
            nombre4: '',
            nombre5: '',
            nombre6: '',
            nombre7: '',
            nombre8: '',
            nombre9: '',
            nombre10: '',
            nombreResultado: '',
            descripcion1: '',
            descripcion2: '',
            descripcion3: '',
            descripcion4: '',
            descripcion5: '',
            descripcion6: '',
            descripcion7: '',
            descripcion8: '',
            descripcion9: '',
            descripcion10: '',
            descripcionResultado: '',
            tipoDato1: '',
            tipoDato2: '',
            tipoDato3: '',
            tipoDato4: '',
            tipoDato5: '',
            tipoDato6: '',
            tipoDato7: '',
            tipoDato8: '',
            tipoDato9: '',
            tipoDato10: '',
            tipoDatoResultado: '',
            tablaVersiones: []
        },
        tablaVersiones: []
    };
    const tipoDatos = [
        {key: 'number', value: 'Número entero'},
        {key: 'decimal', value: 'Número decimal'},
        {key: 'string', value: 'Texto'}
    ];
    
    const [entity, setEntity] = useState(entityInit)
    const [columnas, setColumnas] = useState([])

    const [formValues, _handleFormInput, , formSet] = useForm({
        id: 0,
        codigo: '',
        nombre: '',
        descripcion: '',
        nombreResultado: '',
        descripcionResultado: '',
        tipoDatoResultado: ''
    })

    const [pendingChange, setPendingChange] = useState(false)
    const [countVersionChanges, setCountVersionChange] = useState(0)
    const [loading, setLoading] = useState(false)
    const [definicionOpen, setDefinicionOpen] = useState(params.mode === OPERATION_MODE.NEW)

    const { errorOf, validate } = useFormErrors(getErrors)

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange)

    useEffect(() => {
        if (params.mode !== OPERATION_MODE.NEW) {
            findTabla();
        }
    }, [])

    const hasTablaItems = useMemo(() => {
        const versionesActivas = entity.tablaVersiones.filter(f => f.state !== "r");
        return (versionesActivas.length > 1 || (versionesActivas.length === 1 && versionesActivas[0].tablaItems.some(f => f.state !== 'r')))
    }, [entity.tablaVersiones, countVersionChanges])

    const handleFormInput = (...args) => {
        setPendingChange(true)
        _handleFormInput(...args)
    }

    
    const onSuccessFind = (response) => {
        response.json()
        .then(data => {
            loadData(data)
            setLoading(false)
        })
        .catch(onNoSuccess)
    }

    const onSuccessAdd = (response) => {
        response.json()
        .then(data => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Tabla generada exitosamente', () => {
                const idTablaDefinicion = data.tablaDefinicion.id;
                allowNavigation()
                navigate({
                    to: `/tabla-definicion/edit/${idTablaDefinicion}`,
                    refresh: true,
                    replace: true,
                })
                setLoading(false)
                setPendingChange(false)
            })
        })
        .catch(onNoSuccess)
    }

    const onSuccessModify = (response) => {
        response.json()
        .then(data => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Tabla modificada exitosamente', () => {
                loadData(data)
                setLoading(false)
                setPendingChange(false)
            })
        })
        .catch(onNoSuccess)
    }

    const onNoSuccess = (response) => {
        response.json() 
        .then((error) => { ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message) })
        .catch((error) => { ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error) })
        .finally(() => setLoading(false))
    }

    const onError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setLoading(false)
    }


    const findTabla = (id) => {
        setLoading(true)
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.TABLAS,
            `/${params.id}`,
            null,
            onSuccessFind,
            onNoSuccess,
            onError,
        )
    }

    const addTabla = () => {
        if (!isValid()) return;
        setLoading(true);
        
        const dataBody = {
            tablaDefinicion: {
                id: -1,
                ...getFormValues()
            },
            tablaVersiones: []
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.TABLAS,
            null,
            dataBody,
            onSuccessAdd,
            onNoSuccess,
            onError,
        )
    }

    const modifyTabla = () => {
        if (!isValid()) return;
        setLoading(true);

        const dataBody = {
            tablaDefinicion: {
                id: entity.tablaDefinicion.id,
                ...getFormValues()
            },
            tablaVersiones: entity.tablaVersiones.filter(f => f.state !== 'o').map(y => {
                let x = CloneObject(y);
                x.tablaItems = x.tablaItems.filter(f => f.state !== 'o');
                return x;
            }),
        };

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.TABLAS,
            `/${params.id}`,
            dataBody,
            onSuccessModify,
            onNoSuccess,
            onError,
        )
    }

    const getFormValues = () => {
        return {
            codigo: formValues.codigo,
            nombre: formValues.nombre,
            descripcion: formValues.descripcion,
            nombre1: (columnas[0]?.nombre) ? (columnas[0].nombre) : '',
            nombre2: (columnas[1]?.nombre) ? (columnas[1].nombre) : '',
            nombre3: (columnas[2]?.nombre) ? (columnas[2].nombre) : '',
            nombre4: (columnas[3]?.nombre) ? (columnas[3].nombre) : '',
            nombre5: (columnas[4]?.nombre) ? (columnas[4].nombre) : '',
            nombre6: (columnas[5]?.nombre) ? (columnas[5].nombre) : '',
            nombre7: (columnas[6]?.nombre) ? (columnas[6].nombre) : '',
            nombre8: (columnas[7]?.nombre) ? (columnas[7].nombre) : '',
            nombre9: (columnas[8]?.nombre) ? (columnas[8].nombre) : '',
            nombre10: (columnas[9]?.nombre) ? (columnas[9].nombre) : '',
            nombreResultado: formValues.nombreResultado,
            descripcion1: (columnas[0]?.descripcion) ? (columnas[0].descripcion) : '',
            descripcion2: (columnas[1]?.descripcion) ? (columnas[1].descripcion) : '',
            descripcion3: (columnas[2]?.descripcion) ? (columnas[2].descripcion) : '',
            descripcion4: (columnas[3]?.descripcion) ? (columnas[3].descripcion) : '',
            descripcion5: (columnas[4]?.descripcion) ? (columnas[4].descripcion) : '',
            descripcion6: (columnas[5]?.descripcion) ? (columnas[5].descripcion) : '',
            descripcion7: (columnas[6]?.descripcion) ? (columnas[6].descripcion) : '',
            descripcion8: (columnas[7]?.descripcion) ? (columnas[7].descripcion) : '',
            descripcion9: (columnas[8]?.descripcion) ? (columnas[8].descripcion) : '',
            descripcion10: (columnas[9]?.descripcion) ? (columnas[9].descripcion) : '',
            descripcionResultado: formValues.descripcionResultado,
            tipoDato1: (columnas[0]?.nombre) ? 'string' : '',
            tipoDato2: (columnas[1]?.nombre) ? 'string' : '',
            tipoDato3: (columnas[2]?.nombre) ? 'string' : '',
            tipoDato4: (columnas[3]?.nombre) ? 'string' : '',
            tipoDato5: (columnas[4]?.nombre) ? 'string' : '',
            tipoDato6: (columnas[5]?.nombre) ? 'string' : '',
            tipoDato7: (columnas[6]?.nombre) ? 'string' : '',
            tipoDato8: (columnas[7]?.nombre) ? 'string' : '',
            tipoDato9: (columnas[8]?.nombre) ? 'string' : '',
            tipoDato10: (columnas[9]?.nombre) ? 'string' : '',
            tipoDatoResultado: formValues.tipoDatoResultado
        };
    }

    function getErrors() {
        const errors = {};

        if (!isValidString(formValues.codigo,true)) errors.codigo = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.nombre,true)) errors.nombre = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.descripcion,true)) errors.descripcion = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.tipoDatoResultado,true)) errors.tipoDatoResultado = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (!isValidString(formValues.nombreResultado,true)) errors.nombreResultado = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.descripcionResultado,true)) errors.descripcionResultado = VALIDATION_MESSAGE.REQUIRED;

        return errors;
    }

    const isValid = () => {
        if (!validate()) {
            setDefinicionOpen(true);
            return false;
        }
        if (columnas.length <= 0){
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Debe ingresar al menos una columna');
            return false;        
        }

        const versiones = entity.tablaVersiones.filter(item => item.state !== "r").sort(ComparePeriodos);
        for (let i=0; i < versiones.length; i++) {
            const current = versiones[i];
            if (!isNull(current.fechaDesde) && !isNull(current.fechaHasta) && (current.fechaDesde > current.fechaHasta)) {
                ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Debe ingresar un rango de fechas correcto');
                return false;  
            }

            if (i > 0) {
                const previous = versiones[i-1];
                if (isNull(current.fechaDesde) || isNull(previous.fechaHasta) || previous.fechaHasta >= current.fechaDesde) {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Los rangos de fechas no deben solaparse');
                    return false;  
                }
            }
        }

        return true;
    }

    const loadData = (data) => {
        data.tablaVersiones.forEach(version => {
            version.fechaDesde = version.fechaDesde ? new Date(version.fechaDesde) : null;
            version.fechaHasta = version.fechaHasta ? new Date(version.fechaHasta) : null;
        });

        formSet({
            id: data.tablaDefinicion.id,
            codigo: data.tablaDefinicion.codigo,
            nombre: data.tablaDefinicion.nombre,
            descripcion: data.tablaDefinicion.descripcion,
            nombreResultado: data.tablaDefinicion.nombreResultado,
            descripcionResultado: data.tablaDefinicion.descripcionResultado,
            tipoDatoResultado: data.tablaDefinicion.tipoDatoResultado
        })
        LoadColumnas(data.tablaDefinicion, setColumnas);
        setEntity(data);
    }

    const updateEntity = (typeEntity, row) => {
        // o:original a:add m:modify r:remove c:modify only childs

        let newEntity = CloneObject(entity);

        if (typeEntity === 'TablaVersion') {
            if (row.state === 'a') {
                const tablaVersionesActivas = newEntity.tablaVersiones.filter(item => item.state !== "r").sort(ComparePeriodos);
                if (tablaVersionesActivas.length > 0 && isNull(tablaVersionesActivas[tablaVersionesActivas.length-1].fechaHasta)) {
                    const fechaLimite = !isNull(row.fechaDesde) ? row.fechaDesde : getDateNow();
                    const versionAnterior = tablaVersionesActivas[tablaVersionesActivas.length-1];
                    if (isNull(versionAnterior.fechaDesde) || fechaLimite > versionAnterior.fechaDesde) {
                        versionAnterior.fechaHasta = addDayToDate(fechaLimite,-1);
                        if (versionAnterior.state !== 'a') versionAnterior.state = 'm';
                    }
                }
                newEntity.tablaVersiones.push(row);
            }
            else if (row.state === 'm') {
                const index = newEntity.tablaVersiones.indexOf(newEntity.tablaVersiones.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    newEntity.tablaVersiones[index] = row;
                }
            }
            else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    newEntity.tablaVersiones = newEntity.tablaVersiones.filter(f => f.id !== row.id);
                }
                else {
                    let item = newEntity.tablaVersiones.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
            setCountVersionChange(countVersionChanges + 1);
        }

        setEntity(newEntity);
        setPendingChange(true);
    }

    const onClickVolver = () => {
        navigateWithFilters('/tablas-definicion/');
    }

    return (
        <div>
            <Loading visible={loading} />

            <SectionHeading
                titles={[
                    { title: 'Entidades', },
                    {
                        title: 'Definición de tablas',
                        url: '/tablas-definicion' 
                    },
                    { title: params.mode === OPERATION_MODE.NEW ? 'Nueva' : `Código: ${formValues.codigo}`}
                ]}
            />
          
            <section className="section-accordion">
                <div class="m-top-20 m-bottom-20">
                    <Accordion title={params.mode === OPERATION_MODE.NEW ? "Definición de nueva tabla" : `Definición de "${formValues.nombre}"`} open={definicionOpen} onToggle={setDefinicionOpen}>
                        <div className='row form-basic'>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="codigo" required>Código</FormLabel>
                                <input
                                    name="codigo"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                                    value={formValues.codigo}
                                    onChange={handleFormInput}
                                    maxLength={50}
                                    disabled={params.mode === OPERATION_MODE.VIEW || hasTablaItems}
                                />
                                <FormError message={errorOf('codigo')} />
                            </div>
                            <div className="col-12 col-md-5">
                                <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                                <input
                                    name="nombre"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                                    value={formValues.nombre}
                                    onChange={handleFormInput}
                                    maxLength={50}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                                <FormError message={errorOf('nombre')} />
                            </div>
                            <div className="col-12 col-md-5">
                                <FormLabel htmlFor="descripcion" required>Descripción</FormLabel>
                                <input
                                    name="descripcion"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('descripcion') ? 'is-invalid' : ''}`}
                                    value={formValues.descripcion}
                                    onChange={handleFormInput}
                                    maxLength={250}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                                <FormError message={errorOf('descripcion')} />
                            </div>
                        </div>
                        <div className='row form-basic'>
                            <div className="col-12 col-md-2">
                                <FormLabel htmlFor="tipoDatoResultado" required>Tipo dato resultado</FormLabel>
                                <Select
                                    name="tipoDato"
                                    className={`form-control ${errorOf('tipoDatoResultado') ? 'is-invalid' : ''}`}
                                    value={ formValues.tipoDatoResultado }
                                    options={tipoDatos.map(item => ({ value: item.key, label: item.value }))}
                                    onChange={({ target }) => {
                                        formSet({...formValues,
                                                tipoDatoResultado: target.value,
                                        });
                                        setPendingChange(true)
                                    }}
                                    disabled={params.mode === OPERATION_MODE.VIEW || hasTablaItems}
                                />
                                <FormError message={errorOf('tipoDatoResultado')} />
                            </div>  
                            <div className="col-12 col-md-5">
                                <FormLabel htmlFor="nombreResultado" required>Nombre resultado</FormLabel>
                                <input
                                    name="nombreResultado"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('nombreResultado') ? 'is-invalid' : ''}`}
                                    value={formValues.nombreResultado}
                                    onChange={handleFormInput}
                                    maxLength={50}
                                    disabled={params.mode === OPERATION_MODE.VIEW || hasTablaItems}
                                />
                                <FormError message={errorOf('nombreResultado')} />
                            </div>   
                            <div className="col-12 col-md-5">
                                <FormLabel htmlFor="descripcionResultado" required>Descripción resultado</FormLabel>
                                <input
                                    name="descripcionResultado"
                                    type="text"
                                    placeholder=""
                                    className={`form-control ${errorOf('descripcionResultado') ? 'is-invalid' : ''}`}
                                    value={formValues.descripcionResultado}
                                    onChange={handleFormInput}
                                    maxLength={50}
                                    disabled={params.mode === OPERATION_MODE.VIEW}
                                />
                                <FormError message={errorOf('descripcionResultado')} />
                            </div>                        
                        </div>
                        <div className='row form-basic'>
                            <TablaColumnas 
                                columnas={columnas}
                                setColumnas={setColumnas}
                                params={params}
                                setPendingChange={setPendingChange}
                                disabled={hasTablaItems}
                            />
                        </div>
                    </Accordion>
                    {params.mode !== OPERATION_MODE.NEW &&
                    <Accordion title="Tabla de valores" startOpen={params.mode !== OPERATION_MODE.NEW}>
                        <div className='row'>
                            <TablaVersionesGrid
                                disabled={params.mode !== OPERATION_MODE.EDIT}
                                data={{
                                    idTablaDefinicion: entity.tablaDefinicion.id,
                                    columnas: columnas,
                                    resultado: {
                                        nombre: formValues.nombreResultado,
                                        descripcion: formValues.descripcionResultado,
                                        tipoDato: formValues.tipoDatoResultado
                                    },
                                    list: entity.tablaVersiones
                                }}
                                onChange={(typeEntity, row) => {
                                    updateEntity(typeEntity, row)
                                }}
                            />
                        </div>
                    </Accordion>
                    }
                </div>
            </section>

            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <button className="btn back-button float-start" onClick={onClickVolver}>Volver</button>
                    {params.mode !== OPERATION_MODE.VIEW &&
                    <button
                        className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                        onClick={params.mode === OPERATION_MODE.NEW ? addTabla : modifyTabla}
                        disabled={!pendingChange}
                    >
                        {params.mode === OPERATION_MODE.NEW ? 'Siguiente' : 'Guardar'}
                    </button>
                    }
                </div>
            </footer>
        </div>
    )
}

export default TablaDefinicionView
